import React, { useEffect, useState } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { FiEdit, FiPlus, FiTrash2 } from "react-icons/fi";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
    createExplorePlace, deleteExplorePlace, fetchAvailableExploreThings,
    fetchExplorePlaces, updateExplorePlace,
} from "../../../services/adminRestapiService";
import TopBar from "../../common/TopBar";
import Sidebar from "../../common/Sidebar";
import { getImageUploadError, IMAGE_UPLOAD_ACCEPT, IMAGE_UPLOAD_HELP } from "../../../utils/imageUpload";



const API_URL = "https://api.gosehari.com";
const emptyImageSlots = () => Array(5).fill(null);
const emptyPlace = { thing_id: "", images: emptyImageSlots(), display_order: 0 };

function ExploreImages() {
    const [places, setPlaces] = useState([]);
    const [availableThings, setAvailableThings] = useState([]);
    const [canAdd, setCanAdd] = useState(false);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingPlace, setEditingPlace] = useState(null);
    const [form, setForm] = useState(emptyPlace);
    const [imageFiles, setImageFiles] = useState(emptyImageSlots());
    const [saving, setSaving] = useState(false);
    const token = localStorage.getItem("authToken") || localStorage.getItem("token");
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const toggleSidebar = () => setSidebarOpen((isCurrentlyOpen) => !isCurrentlyOpen);
    const loadPlaces = async () => {
        setLoading(true);
        try { setPlaces((await fetchExplorePlaces(token)).data.data); }
        catch { toast.error("Failed to load Explore Places"); }
        finally { setLoading(false); }
    };
    const loadAvailableThings = async (recordId) => {
        try { setAvailableThings((await fetchAvailableExploreThings(token, recordId)).data.data); }
        catch { toast.error("Failed to load available Top Things"); setAvailableThings([]); }
    };
    const refreshAddAvailability = async () => {
        try { setCanAdd((await fetchAvailableExploreThings(token)).data.data.some((thing) => !thing.is_mapped)); }
        catch { setCanAdd(false); }
    };
    useEffect(() => { loadPlaces(); refreshAddAvailability(); }, []);

    const openAdd = async () => {
        await loadAvailableThings();
        setEditingPlace(null); setForm({ ...emptyPlace, images: emptyImageSlots() }); setImageFiles(emptyImageSlots()); setDialogOpen(true);
    };
    const openEdit = async (place) => {
        await loadAvailableThings(place.id);
        setEditingPlace(place);
        setForm({ thing_id: String(place.thing_id), images: place.images || [place.image_url || null, null, null, null, null], display_order: place.display_order ?? 0 });
        setImageFiles(emptyImageSlots()); setDialogOpen(true);
    };
    const handleChange = ({ target: { name, value } }) => setForm((current) => ({ ...current, [name]: value }));
    const selectImage = (index, file, input) => {
        const imageError = getImageUploadError(file);
        if (imageError) {
            toast.error(imageError);
            input.value = "";
            return;
        }
        setImageFiles((current) => current.map((currentFile, fileIndex) => fileIndex === index ? (file || null) : currentFile));
    };
    const submit = async () => {
        if (!form.thing_id) return toast.error("Select a Top Thing");
        setSaving(true);
        try {
            const payload = new FormData();
            payload.append("thing_id", form.thing_id);
            payload.append("display_order", form.display_order ?? 0);
            payload.append("existing_images", JSON.stringify(form.images));
            payload.append("slot_indexes", JSON.stringify(imageFiles.map((file, index) => file ? index : null).filter((index) => index !== null)));
            imageFiles.forEach((file) => { if (file) payload.append("images", file); });
            if (editingPlace) await updateExplorePlace(token, editingPlace.id, payload);
            else await createExplorePlace(token, payload);
            toast.success(editingPlace ? "Explore Place updated" : "Explore Place created");
            setDialogOpen(false); await loadPlaces(); await refreshAddAvailability();
        } catch (error) {
            toast.error(error.response?.data?.message || "Unable to save Explore Place");
            await loadAvailableThings(editingPlace?.id);
        } finally { setSaving(false); }
    };
    const remove = async (place) => {
        if (!window.confirm(`Delete the Explore Place for “${place.thing_title || place.name || place.thing_id}”?`)) return;
        try { await deleteExplorePlace(token, place.id); toast.success("Explore Place deleted; its Top Thing is available again"); await loadPlaces(); await refreshAddAvailability(); }
        catch (error) { toast.error(error.response?.data?.message || "Unable to delete Explore Place"); }
    };

    const columns = [
        { field: "id", headerName: "ID", width: 80 },
        { field: "thing_title", headerName: "Top Thing", flex: 1.5, minWidth: 200 },
        { field: "thing_category", headerName: "Category", width: 140 },
        {
            field: "images",
            headerName: "Display images (5 slots)",
            width: 410,
            sortable: false,
            renderCell: ({ value = [], row }) => <div className="flex gap-1 items-center h-full">{Array.from({ length: 5 }, (_, index) => value[index] ? <img key={index} src={value[index].startsWith("http") ? value[index] : `${API_URL}${value[index]}`} alt={`${row.thing_title || "Explore place"} ${index + 1}`} className="w-14 h-14 object-cover rounded border" /> : <div key={index} className="w-14 h-14 rounded border border-dashed border-gray-400 bg-gray-100 text-[10px] text-gray-500 flex items-center justify-center">Empty</div>)}</div>,
        },
        { field: "display_order", headerName: "Order", width: 90 },
        { field: "actions", headerName: "Actions", width: 130, sortable: false, renderCell: ({ row }) => <Box><IconButton aria-label="Edit Explore Place" color="primary" onClick={() => openEdit(row)}><FiEdit /></IconButton><IconButton aria-label="Delete Explore Place" color="error" onClick={() => remove(row)}><FiTrash2 /></IconButton></Box> },
    ];
    const hasAvailableThings = availableThings.some((thing) => !thing.is_mapped);
    const selectedTopThing = availableThings.find((thing) => String(thing.id) === String(form.thing_id));

    return (
        <div className="flex min-h-screen bg-slate-50">
            <ToastContainer position="top-right" autoClose={3000} theme="colored" />

            <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

            <div
                className={`min-h-screen min-w-0 flex-1 transition-[margin] duration-300 ${sidebarOpen ? "lg:ml-64" : "lg:ml-20"}`}
            >
                <TopBar toggleSidebar={toggleSidebar} sidebarOpen={sidebarOpen} />

                <main className="space-y-6 px-4 pb-8 pt-24 sm:px-6 lg:space-y-10 lg:px-8">
                    <section className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm sm:p-5">

                        <ToastContainer position="top-right" autoClose={3000} theme="colored" />
                        <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"><div><h1 className="text-xl font-semibold text-slate-900 sm:text-2xl">Explore Places</h1><p className="mt-1 text-sm text-slate-600">Each Top Thing can be mapped to one Explore Place only.</p>{!loading && !canAdd && <p className="mt-1 text-sm text-amber-700">All Top Things are already mapped.</p>}</div><Button variant="contained" startIcon={<FiPlus />} onClick={openAdd} disabled={loading || !canAdd} className="self-start sm:self-auto">Add Explore Place</Button></div>
                        <div className="min-w-0 overflow-x-auto rounded-xl border border-slate-100"><DataGrid rows={places} columns={columns} loading={loading} autoHeight rowHeight={68} disableRowSelectionOnClick pageSizeOptions={[10, 25, 50]} initialState={{ pagination: { paginationModel: { pageSize: 10 } } }} sx={{ minWidth: 800, border: 0 }} /></div>
                        <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} fullWidth maxWidth="sm"><DialogTitle>{editingPlace ? "Edit Explore Place" : "Add Explore Place"}</DialogTitle><DialogContent>
                            {!editingPlace && !hasAvailableThings ? <p className="py-4 text-gray-700">All Top Things are already mapped.</p> : <Box component="form" className="flex flex-col gap-4 pt-3">
                        {editingPlace ? (
                            <div>
                                <span className="block text-sm mb-1">Top Thing</span>
                                <div className="w-full rounded border bg-gray-100 p-3 text-gray-700">
                                    {editingPlace.thing_title || editingPlace.name || `Top Thing #${editingPlace.thing_id}`}
                                </div>
                            </div>
                        ) : (
                            <><label><span className="block text-sm mb-1">Top Thing *</span><select name="thing_id" value={form.thing_id} onChange={handleChange} className="w-full border rounded p-3" required><option value="">Select a Top Thing</option>{availableThings.map((thing) => <option key={thing.id} value={thing.id} disabled={thing.is_mapped}>{thing.title} ({thing.category}){thing.is_mapped ? " — Already mapped" : ""}</option>)}</select></label>
                            {selectedTopThing && <div className="rounded border bg-gray-50 p-3"><div className="flex gap-3"><div className="relative h-20 w-28 shrink-0 overflow-hidden rounded bg-gray-200"><span className="absolute inset-0 flex items-center justify-center text-xs text-gray-500">No image</span>{selectedTopThing.image && <img src={selectedTopThing.image.startsWith("http") ? selectedTopThing.image : `${API_URL}${selectedTopThing.image}`} alt={selectedTopThing.title} className="relative h-full w-full object-cover" onError={(event) => { event.currentTarget.style.display = "none"; }} />}</div><div className="min-w-0"><p className="font-semibold text-gray-800">{selectedTopThing.title}</p><p className="text-sm text-gray-600">{selectedTopThing.category}</p><p className="mt-1 text-sm text-gray-600">{selectedTopThing.description || "No description available."}</p></div></div></div>}</>
                        )}
                                <div><p className="text-sm mb-2">Display images (up to 5)</p><div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{Array.from({ length: 5 }, (_, index) => <label key={index} className="border rounded p-2 text-sm"><span className="block mb-1">Image {index + 1}</span>{imageFiles[index] ? <img src={URL.createObjectURL(imageFiles[index])} alt={`New upload ${index + 1}`} className="w-full h-24 object-cover rounded" /> : form.images[index] ? <img src={form.images[index].startsWith("http") ? form.images[index] : `${API_URL}${form.images[index]}`} alt={`Display ${index + 1}`} className="w-full h-24 object-cover rounded" /> : <div className="h-24 border border-dashed rounded bg-gray-50 flex items-center justify-center text-gray-400">Empty image slot</div>}<input className="block mt-2 w-full" type="file" accept={IMAGE_UPLOAD_ACCEPT} onChange={(event) => selectImage(index, event.target.files[0], event.target)} /></label>)}</div><p className="mt-1 text-xs text-gray-500">{IMAGE_UPLOAD_HELP}</p></div>
                                <TextField label="Display order" name="display_order" value={form.display_order} onChange={handleChange} type="number" fullWidth />
                            </Box>}
                        </DialogContent><DialogActions><Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>{(editingPlace || hasAvailableThings) && <Button variant="contained" onClick={submit} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>}</DialogActions></Dialog>
                    </section>
                </main>
            </div>
        </div>
    );
}

export default ExploreImages;
